import React, { useEffect, useState } from 'react';
import { Store, StoreOpeningTime } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import Input from 'components/commercetools-ui/atoms/input';
import Radio from 'components/commercetools-ui/atoms/radio';
import Skeleton from 'components/commercetools-ui/atoms/skeleton';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';
import useConfig from 'frontastic/hooks/useConfig';
import useCTStock from 'frontastic/hooks/useCTStock';
import initialStores from '../../data';

interface StoreListProps {
  setSearchKeyword: (keyword: string) => void;
  setSelectedStore: (store: Store) => void;
  selectedStore: Store | null;
  handleStoreAddress: () => void;
  storeOpeningTime: StoreOpeningTime | null;
}

const StoreList: React.FC<StoreListProps> = ({
  setSelectedStore,
  setSearchKeyword,
  selectedStore,
  handleStoreAddress,
  storeOpeningTime,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [stores, setStores] = useState<Store[]>(initialStores);
  const [isEstimateLoading, setIsEstimateLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { getStoreStock } = useCTStock();
  const { config } = useConfig();
  const { data, getStores, productSKUs } = useCart();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const { formatMessage } = useFormat({ name: 'checkout' });
  const [sortedStores, setSortedStores] = useState<Store[]>([]);
  const [filteredAndSortedStores, setFilteredAndSortedStores] = useState<Store[]>([]);

  useEffect(() => {
    if (selectedStore) {
      setSelectedStoreId(selectedStore.ID);
      scrollToSelectedStore(selectedStore.ID);
    }
  }, [selectedStore]);

  useEffect(() => {
    const fetchStoresAndRelatedData = async () => {
      try {
        if (!getStores) return;
        const storeData = await getStores();
        if (!storeData.length) {
          setStores(initialStores);
          setIsLoading(false);
          return;
        }
        setStores(storeData);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStoresAndRelatedData();
  }, [getStores]);

  useEffect(() => {
    const fetchStoresAndRelatedData = async () => {
      try {
        if (!getStores) return;

        const storeDataPromise = getStores();
        const configPromise = config();

        const [storeData, configObj] = await Promise.all([storeDataPromise, configPromise]);

        if (!storeData.length) {
          setStores([]);
          setIsEstimateLoading(false);
          return;
        }

        const storeIDs = storeData.map((store) => store.ID);
        const stocksPromise = getStoreStock(storeIDs, productSKUs as string[]);

        const dispatchDate = new Date();
        dispatchDate.setDate(dispatchDate.getDate() + configObj.customFitETAInDays);
        const formattedDispatchDate = `${dispatchDate.getDate()}/${
          dispatchDate.getMonth() + 1
        }/${dispatchDate.getFullYear()}`;

        const dispatchDatePersonlisedProduct = new Date();
        dispatchDate.setDate(dispatchDatePersonlisedProduct.getDate() + configObj.personalisedProductETAInDays);
        const formattedDispatchDatePersonlisedProduct = `${dispatchDatePersonlisedProduct.getDate()}/${
          dispatchDatePersonlisedProduct.getMonth() + 1
        }/${dispatchDatePersonlisedProduct.getFullYear()}`;

        const stocks = await stocksPromise;

        const updatedStores = storeData.map((store) => {
          const stock = stocks.find((stockItem) => stockItem.storeRef === store.ID);

          if (data?.lineItems?.some((item) => item.isCustomFit)) {
            store.stockMessage = `Estimated collection date: ${formattedDispatchDate}`;
            return store;
          }

          if (data?.lineItems?.some((item) => item.isPersonalized)) {
            store.stockMessage = `Estimated collection date: ${formattedDispatchDatePersonlisedProduct}`;
            return store;
          }

          const allProductsInStock = data?.lineItems?.every((item) => {
            const productStock = stock?.productsStock.find((product) => product.sku === item.variant?.sku);
            return productStock && productStock.quantity >= (item.count as number);
          });

          if (stock && stock.inStock && allProductsInStock) {
            store.stockMessage = 'Collect after 2hrs';
          }

          return store;
        });

        setStores(updatedStores);
        setIsEstimateLoading(false);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
        setIsEstimateLoading(false);
      }
    };

    fetchStoresAndRelatedData();
  }, [getStores, config, productSKUs, data?.lineItems]);

  useEffect(() => {
    setSortedStores([...stores].sort((a, b) => a.name.localeCompare(b.name)));
  }, [stores]);

  useEffect(() => {
    if (sortedStores) {
      setFilteredAndSortedStores(sortedStores);
    }
  }, [sortedStores]);

  const scrollToSelectedStore = (storeId: string) => {
    const element = document.getElementById(`store-${storeId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
  };

  const handleSearchSubmit = async () => {
    if (!searchTerm) {
      setFilteredAndSortedStores(sortedStores);
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ address: searchTerm });
      const { location } = response.results[0].geometry;
      const userLat = location.lat();
      const userLng = location.lng();

      const exactMatches = sortedStores.filter((store) => store.name.toLowerCase().includes(searchTerm.toLowerCase()));

      const sortedExactMatches = exactMatches.sort((a, b) => {
        const distanceA = getDistanceFromLatLonInKm(userLat, userLng, a.latitude, a.longitude);
        const distanceB = getDistanceFromLatLonInKm(userLat, userLng, b.latitude, b.longitude);
        return distanceA - distanceB;
      });

      const filteredStores = sortedStores.filter((store) => {
        const distance = getDistanceFromLatLonInKm(userLat, userLng, store.latitude, store.longitude);
        return distance <= 60 && !exactMatches.includes(store);
      });

      const sortedFilteredStores = filteredStores.sort((a, b) => {
        const distanceA = getDistanceFromLatLonInKm(userLat, userLng, a.latitude, a.longitude);
        const distanceB = getDistanceFromLatLonInKm(userLat, userLng, b.latitude, b.longitude);
        return distanceA - distanceB;
      });

      const finalStores = [...sortedExactMatches, ...sortedFilteredStores];

      setFilteredAndSortedStores(finalStores);
      setSearchKeyword(searchTerm);
    } catch (error) {
      console.warn('Geocoding error:', error);
      setFilteredAndSortedStores([]);
    }
  };

  const handleSelectStore = (store: Store) => {
    setSelectedStoreId(store.ID);
    setSelectedStore(store);
  };

  return (
    <div className="flex  w-fit flex-col p-20 md:h-[580px] md:w-auto md:p-0">
      <div className="sticky mb-20 flex items-center gap-2">
        <div className="icon icon-currentlocation absolute left-14 top-14 z-10 border-r border-input-border pr-12 text-lg text-ag_color-red"></div>
        <Input
          name="StoreSearch"
          type="string"
          placeholder="Town or postcode"
          value={searchTerm as string}
          onChange={handleSearchChange}
          className="rounded-e-none !pr-[10px] pl-[50px] text-[16px]"
          variant="primary"
        />
        <Button className="-ml-4 mt-7 rounded-s-none p-2 uppercase" onClick={handleSearchSubmit}>
          {formatMessage({ id: 'find.postcode.or.town', defaultMessage: 'Find' })}
        </Button>
      </div>
      {isLoading ? (
        <ul className="overflow-y-scroll">
          {[1, 2, 3, 4, 5].map((item) => {
            return (
              <div key={item} className="relative mb-30">
                <div className="relative mb-[12px] h-20 w-[70%]">
                  <Skeleton></Skeleton>
                </div>
                <div className="relative h-60 w-[90%]">
                  <Skeleton></Skeleton>
                </div>
              </div>
            );
          })}
        </ul>
      ) : (
        <ul className="overflow-y-scroll">
          {filteredAndSortedStores &&
            filteredAndSortedStores.map((store: Store, index: number) => (
              <>
                {store.ID && (
                  <li key={index} id={`store-${store.ID}`} className="mb-20 border-b border-input-border pb-20">
                    <label className="flex cursor-pointer items-start gap-4">
                      <div className="relative mr-20">
                        <Radio
                          name="store"
                          value={store.ID}
                          checked={selectedStoreId === store.ID}
                          onChange={() => handleSelectStore(store)}
                          className="absolute top-0"
                        />
                      </div>
                      <div className="ml-20 flex flex-col justify-start">
                        <h2
                          className={`text-base font-medium text-primary-black ${
                            selectedStoreId === store.ID ? '!text-ag_color-text_green' : ''
                          }`}
                        >
                          {store.name}
                        </h2>
                        <p className="mt-4 text-sm font-light leading-5">{store.listAddress}</p>
                        {isEstimateLoading ? (
                          <div className="relative h-18 w-[80%]">
                            <Skeleton></Skeleton>
                          </div>
                        ) : (
                          <p className="mt-4 hidden text-sm text-ag_color-text_green md:block">{store.stockMessage}</p>
                        )}
                        {selectedStoreId === store.ID && (
                          <>
                            <div className="mt-8 block md:hidden">
                              <table>
                                {storeOpeningTime &&
                                  storeOpeningTime.storeHours &&
                                  Object?.entries(storeOpeningTime.storeHours).map(([dayKey, hours]) => {
                                    const dayName = storeOpeningTime.hourResources
                                      ? storeOpeningTime.hourResources[dayKey]
                                      : dayKey;

                                    return (
                                      <tr key={dayKey} className="border-b">
                                        <td className="py-4 text-xs font-medium capitalize">{dayName}</td>
                                        <td className="py-4 text-right text-xs font-light">{hours}</td>
                                      </tr>
                                    );
                                  })}
                              </table>
                              {store.phone && <p className="mt-16 text-sm font-light">{store.phone}</p>}
                              {store.email && <p className="mt-8 text-sm font-light">{store.email}</p>}
                            </div>
                            <div className="mt-12">
                              <Button
                                size="xs"
                                className="w-full px-20 py-10 uppercase md:w-auto"
                                onClick={handleStoreAddress}
                              >
                                {formatMessage({
                                  id: 'collect.from.this.store',
                                  defaultMessage: 'Collect from this store',
                                })}
                              </Button>
                            </div>
                          </>
                        )}
                        <p className="mt-4 block text-sm text-ag_color-text_green md:hidden">{store.stockMessage}</p>
                      </div>
                    </label>
                  </li>
                )}
              </>
            ))}
        </ul>
      )}

      {filteredAndSortedStores.length === 0 && (
        <>
          <h1 className="text-center text-[20px] font-light capitalize">
            {formatMessage({
              id: 'store.no.result',
              defaultMessage: "We're sorry, we couldn't find results for your search",
            })}
          </h1>
          <Button
            variant="ghost"
            className="font-medium text-ag_color-text_green hover:underline"
            onClick={() => {
              setSearchTerm('');
              setFilteredAndSortedStores(sortedStores);
            }}
          >
            Clear search
          </Button>
        </>
      )}
    </div>
  );
};

export default StoreList;
